import type { Listing, ShopListing } from "@/types";
import { shopClickUrl } from "@/lib/shopClickUrl";

interface Props {
  listings: Listing[];
  shopListings: ShopListing[];
}

export function PriceHeroStrip({ listings, shopListings }: Props) {
  const cheapestListing = listings
    .filter((l) => l.status === "active" && l.price != null)
    .reduce<Listing | null>((min, l) => {
      if (!min) return l;
      return (l.price as number) < (min.price as number) ? l : min;
    }, null);

  const cheapestShop = shopListings
    .filter((s) => s.price != null)
    .reduce<ShopListing | null>((min, s) => {
      if (!min) return s;
      return (s.price as number) < (min.price as number) ? s : min;
    }, null);

  if (!cheapestListing && !cheapestShop) return null;

  const listingPrice = cheapestListing?.price ?? null;
  const shopPrice = cheapestShop?.price ?? null;
  const savings =
    listingPrice != null && shopPrice != null && shopPrice > listingPrice
      ? shopPrice - listingPrice
      : null;
  const savingsPct = savings != null && shopPrice != null
    ? Math.round((savings / shopPrice) * 100)
    : null;

  return (
    <div className="mt-4 flex flex-wrap items-stretch gap-3 rounded-2xl border border-primary-200 bg-gradient-to-r from-primary-50 to-white p-4">
      {cheapestListing && (
        <a
          href={`/listings/${cheapestListing.id}`}
          className="group flex flex-1 items-center gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-gray-200 transition-shadow hover:shadow-md"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7l9-4 9 4-9 4-9-4z M3 7v10l9 4 9-4V7" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wider text-emerald-700">
              Pigiausias skelbimas
            </p>
            <p className="text-2xl font-bold leading-tight text-gray-900">
              {(listingPrice as number).toFixed(2)} €
            </p>
            <p className="truncate text-xs text-gray-500">
              {cheapestListing.conditionLabel ?? "Naudotas"} · {cheapestListing.seller?.displayName ?? "skelbėjas"}
            </p>
          </div>
        </a>
      )}

      {cheapestShop && (
        <a
          href={shopClickUrl(cheapestShop.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-1 items-center gap-3 rounded-xl bg-white px-4 py-3 ring-1 ring-gray-200 transition-shadow hover:shadow-md"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-700">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Parduotuvėje nuo
            </p>
            <p className="text-2xl font-bold leading-tight text-gray-900">
              {(shopPrice as number).toFixed(2)} €
            </p>
            <p className="truncate text-xs text-gray-500">{cheapestShop.shopName}</p>
          </div>
        </a>
      )}

      {savings != null && savingsPct != null && savingsPct >= 5 && (
        <div className="flex flex-1 items-center gap-3 rounded-xl bg-emerald-600 px-4 py-3 text-white sm:flex-none">
          <svg className="h-7 w-7 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider opacity-90">Sutaupai</p>
            <p className="text-2xl font-bold leading-tight">{savings.toFixed(2)} €</p>
            <p className="text-xs opacity-90">−{savingsPct}% vs. parduotuvėje</p>
          </div>
        </div>
      )}
    </div>
  );
}
